import type { TrackingMeta } from '../components/Trackable/Trackable';

export type TrackingHostPolicy =
  | 'flow-defaults'
  | 'explicit-node-enable'
  | 'registered-defaults';

/**
 * True when the host supplies registered defaults. Those hosts turn
 * tracking on unless a node opts out.
 */
export const isDefaultOnHostPolicy = (
  policy: TrackingHostPolicy | undefined,
): boolean => policy === 'registered-defaults';

export interface HostFormContext {
  readonly formId: string;
  readonly formName: string;
  readonly formType: string;
}

export interface TrackingHostContext {
  /** Metadata-only resolve root. Registered-default hosts never store the raw page. */
  readonly page: Readonly<Record<string, unknown>>;
  readonly dedupScope: {
    readonly sessionId: string;
    readonly configId: string;
  };
  readonly policy: TrackingHostPolicy;
  /**
   * Host-owned intersect threshold. When omitted, element view tracking
   * uses the generic default of 1 (the whole element must be visible).
   */
  readonly viewThreshold?: number;
  readonly formContext?: HostFormContext;
  readonly componentTrackingDefaults?: Readonly<Record<string, TrackingMeta>>;
}

/**
 * Whether the mounting host only dispatches for nodes that opt in
 * (`kind: 'element'` + `enabled: true`). Package-internal: the
 * tracking surface branches on the host's policy, consumers of
 * `useTracking()` never need to.
 */
export const requiresExplicitNodeEnable = (
  host: TrackingHostContext | undefined,
): boolean => host?.policy === 'explicit-node-enable';
